import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { DISTRIBUTOR } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Distributors() {
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    business_type: '',
    monthly_volume: '',
    message: '',
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/distributors`, form);
      toast.success(t('distributors.success'));
      setDone(true);
    } catch (err) {
      toast.error(t('distributors.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FFFBFB]">
      <section className="px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#FF7597]">
              {t('distributors.eyebrow')}
            </p>
            <h1 className="font-display mt-3 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#1F1A24] text-balance">
              {t('distributors.title')}
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#574A66] leading-relaxed max-w-xl">
              {t('distributors.subtitle')}
            </p>

            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {['+20 años', 'B2B 48h', '12 países'].map((s, i) => (
                <div key={i} className="rounded-3xl bg-[#FFF2F5] border border-[#FF7597]/15 p-4 text-center">
                  <p className="font-display font-black text-[#FF7597] tracking-tighter">{s}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] bg-white border border-[#FF7597]/15 p-6 md:p-10 shadow-[0_24px_60px_rgba(255,117,151,0.10)]">
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{t('distributors.formTitle')}</h2>

            {done ? (
              <div className="mt-10 flex flex-col items-center text-center py-10">
                <span className="h-16 w-16 rounded-full bg-[#FFF2F5] inline-flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-[#FF7597]" />
                </span>
                <p className="font-display text-2xl font-bold text-[#1F1A24]">{t('distributors.success')}</p>
              </div>
            ) : (
              <form data-testid={DISTRIBUTOR.form} onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t('distributors.businessName')} testId={DISTRIBUTOR.businessName} value={form.business_name} onChange={update('business_name')} required />
                <Field label={t('distributors.contactName')} testId={DISTRIBUTOR.contactName} value={form.contact_name} onChange={update('contact_name')} required />
                <Field label={t('distributors.email')} type="email" testId={DISTRIBUTOR.email} value={form.email} onChange={update('email')} required />
                <Field label={t('distributors.phone')} testId={DISTRIBUTOR.phone} value={form.phone} onChange={update('phone')} required />
                <Field label={t('distributors.country')} testId={DISTRIBUTOR.country} value={form.country} onChange={update('country')} required />
                <Field label={t('distributors.city')} testId={DISTRIBUTOR.city} value={form.city} onChange={update('city')} required />

                <SelectField
                  label={t('distributors.businessType')}
                  testId={DISTRIBUTOR.businessType}
                  value={form.business_type}
                  onChange={update('business_type')}
                  options={[
                    { v: '', l: t('distributors.businessTypePlaceholder') },
                    { v: 'boutique', l: t('distributors.bt_boutique') },
                    { v: 'pharmacy', l: t('distributors.bt_pharmacy') },
                    { v: 'online', l: t('distributors.bt_online') },
                    { v: 'chain', l: t('distributors.bt_chain') },
                    { v: 'other', l: t('distributors.bt_other') },
                  ]}
                />

                <SelectField
                  label={t('distributors.monthlyVolume')}
                  testId={DISTRIBUTOR.monthlyVolume}
                  value={form.monthly_volume}
                  onChange={update('monthly_volume')}
                  options={[
                    { v: '', l: t('distributors.businessTypePlaceholder') },
                    { v: '<100', l: t('distributors.vol_1') },
                    { v: '100-500', l: t('distributors.vol_2') },
                    { v: '500-2000', l: t('distributors.vol_3') },
                    { v: '>2000', l: t('distributors.vol_4') },
                  ]}
                />

                <div className="sm:col-span-2">
                  <Label>{t('distributors.message')}</Label>
                  <textarea
                    data-testid={DISTRIBUTOR.message}
                    value={form.message}
                    onChange={update('message')}
                    rows={4}
                    className="w-full rounded-2xl border border-[#FF7597]/20 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#FF7597] focus:ring-2 focus:ring-[#FF7597]/20 transition-all"
                  />
                </div>

                <button
                  data-testid={DISTRIBUTOR.submit}
                  type="submit"
                  disabled={submitting}
                  className="sm:col-span-2 mt-2 inline-flex items-center justify-center px-7 py-4 rounded-full bg-[#FF7597] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] hover:scale-[1.02] transition-all disabled:opacity-60 disabled:hover:scale-100 shadow-[0_12px_40px_rgba(255,117,151,0.35)]"
                >
                  {submitting ? t('distributors.submitting') : t('distributors.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="block text-[10px] font-bold tracking-[0.25em] uppercase text-[#574A66] mb-2">{children}</label>
);

const Field = ({ label, testId, value, onChange, type = 'text', required }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full rounded-2xl border border-[#FF7597]/20 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#FF7597] focus:ring-2 focus:ring-[#FF7597]/20 transition-all"
    />
  </div>
);

const SelectField = ({ label, testId, value, onChange, options }) => (
  <div>
    <Label>{label}</Label>
    <select
      data-testid={testId}
      value={value}
      onChange={onChange}
      required
      className="w-full rounded-2xl border border-[#FF7597]/20 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#FF7597] focus:ring-2 focus:ring-[#FF7597]/20 transition-all"
    >
      {options.map((o) => (
        <option key={o.v} value={o.v} disabled={o.v === ''}>{o.l}</option>
      ))}
    </select>
  </div>
);
